'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { HomepageFeature } from '@telegamez/types';
import { trackHomepageEvent, type HomepageAnalyticsEvent, type HomepageAnalyticsParameters } from '@/lib/homepage-analytics';

export function useHomepageAnalytics(preview: boolean, trackPageView = true) {
  const startedAt = useRef<number | null>(null);
  const ended = useRef(false);
  const viewed = useRef(false);
  const context = useRef<HomepageAnalyticsParameters>({ phase: 'entry' });
  const used = useRef(new Set<HomepageFeature>());
  const chapters = useRef(new Set<HomepageFeature>());

  const track = useCallback((event: HomepageAnalyticsEvent, parameters: HomepageAnalyticsParameters = {}) => {
    if (preview) return;
    if (event === 'experience_action' && parameters.success && parameters.feature && parameters.action !== 'open_panel') used.current.add(parameters.feature);
    if (event === 'reaction' && parameters.success) used.current.add('reactions');
    const elapsed = startedAt.current === null ? 0 : Math.min(300, Math.max(0, Math.floor((Date.now() - startedAt.current) / 1000)));
    trackHomepageEvent(event, { ...context.current, elapsed_seconds: elapsed, ...parameters });
  }, [preview]);

  const finish = useCallback((reason: 'expired' | 'leave' | 'navigation') => {
    if (startedAt.current === null || ended.current) return;
    ended.current = true;
    track('end', { end_reason: reason, experiences_used: used.current.size });
  }, [track]);

  const start = useCallback(() => {
    if (startedAt.current !== null) return;
    startedAt.current = Date.now();
    context.current = { phase: 'conversation' };
    track('start');
  }, [track]);


  useEffect(() => {
    if (trackPageView && !viewed.current) { viewed.current = true; track('view'); }
    const leave = () => finish('navigation');
    window.addEventListener('pagehide', leave);
    return () => { window.removeEventListener('pagehide', leave); leave(); };
  }, [finish, track, trackPageView]);

  const experience = useCallback((feature: HomepageFeature) => {
    context.current = { phase: 'conversation', feature };
    if (chapters.current.has(feature)) return;
    chapters.current.add(feature);
    track('experience_view', { feature });
  }, [track]);
  return { track, start, experience, finish };
}
